<template>
  <div class="list-page-container">
    <!-- 顶部操作区 -->
    <div class="control-panel">
      <div class="control-container">
        <div class="header-info">
          <i class="el-icon-cpu"></i>
          <span>{{ $t('resourceManagement.modelProviderTips') }}</span>
        </div>
        <div class="action-wrapper">
          <el-button type="primary" @click="handleAdd" v-if="hasPermission('sys:modelprovider:save')">
            <i class="el-icon-plus"></i> {{ $t('common.add') }}
          </el-button>
        </div>
      </div>
    </div>

    <!-- 筛选模块 -->
    <div class="control-panel has-query">
      <div class="control-container">
        <el-form :inline="true" :model="searchForm" class="filter-form" @submit.native.prevent>
          <el-form-item :label="$t('providerManagement.categoryFilter')">
            <el-select v-model="searchForm.modelType" clearable style="width: 150px" @change="handleSearch">
              <el-option :label="$t('providerManagement.all')" value=""></el-option>
              <el-option v-for="(label, key) in $t('providerManagement.modelType')" :key="key" :label="label" :value="key"></el-option>
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-input
              v-model="searchForm.name"
              :placeholder="$t('providerManagement.searchPlaceholder')"
              clearable
              class="filter-input"
              @keyup.enter.native="handleSearch"
            >
              <i slot="prefix" class="el-input__icon el-icon-search"></i>
            </el-input>
          </el-form-item>
          <div class="search-btns">
            <el-button type="primary" @click="handleSearch" icon="el-icon-search">{{ $t('common.search') }}</el-button>
            <el-button @click="handleReset" icon="el-icon-refresh">{{ $t('common.reset') }}</el-button>
          </div>
        </el-form>
      </div>
    </div>

    <!-- 数据列表 -->
    <el-card class="list-card" shadow="never">
      <el-table
        :data="providerList"
        v-loading="loading"
        height="100%"
        style="width: 100%"
        class="custom-table"
        @selection-change="handleSelectionChange"
      >
        <el-table-column type="selection" width="55"></el-table-column>
        <el-table-column prop="modelType" :label="$t('providerManagement.category')" width="200" align="center">
          <template slot-scope="scope">
            <el-tag size="small" effect="plain">{{ getModelTypeLabel(scope.row.modelType) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="providerCode" :label="$t('providerManagement.providerCode')" width="150" align="center"></el-table-column>
        <el-table-column prop="name" :label="$t('common.name')" min-width="150" align="center"></el-table-column>
        <el-table-column prop="fieldConfig" :label="$t('providerManagement.fieldConfig')" width="120" align="center">
          <template slot-scope="scope">
            <el-popover placement="left" width="400" trigger="click">
              <el-table :data="parseFieldConfig(scope.row.fieldConfig)" size="mini">
                <el-table-column prop="key" :label="$t('providerDialog.fieldKey')" width="100"></el-table-column>
                <el-table-column prop="label" :label="$t('providerDialog.fieldLabel')" width="120"></el-table-column>
                <el-table-column prop="type" :label="$t('providerDialog.fieldType')" width="80"></el-table-column>
                <el-table-column prop="defaultValue" :label="$t('providerDialog.defaultValue')"></el-table-column>
              </el-table>
              <el-button slot="reference" type="text" class="view-fields-btn">{{ $t('providerManagement.viewFields') }}</el-button>
            </el-popover>
          </template>
        </el-table-column>
        <el-table-column prop="sort" :label="$t('common.sort')" width="80" align="center"></el-table-column>
        <el-table-column v-if="hasAnyActionPermission" :label="$t('common.actions')" :width="actionColumnWidth" align="center" fixed="right">
          <template slot-scope="scope">
            <div class="action-buttons">
              <el-button type="primary" size="mini" plain @click="handleEdit(scope.row)" v-if="hasPermission('sys:modelprovider:update')">
                {{ $t('common.edit') }}
              </el-button>
              <el-button type="danger" size="mini" plain class="delete-btn" @click="handleDelete(scope.row)" v-if="hasPermission('sys:modelprovider:delete')">
                {{ $t('common.delete') }}
              </el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-container">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="currentPage"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
        ></el-pagination>
      </div>
    </el-card>

    <!-- 底部悬浮批量操作栏 -->
    <transition name="el-zoom-in-bottom">
      <div v-if="selectedItems.length > 0" class="batch-footer-bar">
        <div class="batch-info">
          <i class="el-icon-info"></i>
          <span>{{ $t('userManagement.selectedCount', { count: selectedItems.length }) }}</span>
        </div>
        <div class="batch-buttons">
          <el-button type="danger" size="small" icon="el-icon-delete" @click="handleBatchDelete" v-if="hasPermission('sys:modelprovider:delete')">
            {{ $t('common.batchDelete') }}
          </el-button>
          <el-divider direction="vertical"></el-divider>
          <el-button type="text" @click="selectedItems = []">{{ $t('common.cancel') }}</el-button>
        </div>
      </div>
    </transition>

    <!-- 新增/编辑弹窗 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="800px" custom-class="custom-dialog">
      <el-form :model="providerForm" :rules="rules" ref="providerForm" label-width="100px" class="dialog-content">
        <div class="form-row">
          <el-form-item :label="$t('providerDialog.category')" prop="modelType" style="flex: 1">
            <el-select v-model="providerForm.modelType" :placeholder="$t('providerDialog.selectCategory')" style="width: 100%">
              <el-option v-for="(label, key) in $t('providerManagement.modelType')" :key="key" :label="label" :value="key"></el-option>
            </el-select>
          </el-form-item>
          <el-form-item :label="$t('providerDialog.sort')" prop="sort" style="width: 150px">
            <el-input-number v-model="providerForm.sort" :min="0" style="width: 100%"></el-input-number>
          </el-form-item>
        </div>
        <div class="form-row">
          <el-form-item :label="$t('providerDialog.code')" prop="providerCode" style="flex: 1">
            <el-input v-model="providerForm.providerCode" :placeholder="$t('providerDialog.inputCode')"></el-input>
          </el-form-item>
          <el-form-item :label="$t('providerDialog.name')" prop="name" style="flex: 1">
            <el-input v-model="providerForm.name" :placeholder="$t('providerDialog.inputName')"></el-input>
          </el-form-item>
        </div>
        
        <el-divider content-position="left">{{ $t('providerDialog.fieldConfig') }}</el-divider>
        
        <div class="field-actions">
          <el-button type="primary" size="mini" icon="el-icon-plus" @click="addField">{{ $t('providerDialog.add') }}</el-button>
          <el-button type="danger" size="mini" icon="el-icon-delete" @click="batchDeleteFields" v-if="selectedFields.length > 0">{{ $t('providerDialog.batchDelete') }}</el-button>
        </div>

        <el-table :data="fieldList" size="small" border style="margin-top: 10px" @selection-change="handleFieldSelectionChange" class="custom-table-small">
          <el-table-column type="selection" width="40"></el-table-column>
          <el-table-column :label="$t('providerDialog.fieldKey')" min-width="120">
            <template slot-scope="scope">
              <el-input v-if="scope.row.editing" v-model="scope.row.key" size="mini"></el-input>
              <span v-else>{{ scope.row.key }}</span>
            </template>
          </el-table-column>
          <el-table-column :label="$t('providerDialog.fieldLabel')" min-width="120">
            <template slot-scope="scope">
              <el-input v-if="scope.row.editing" v-model="scope.row.label" size="mini"></el-input>
              <span v-else>{{ scope.row.label }}</span>
            </template>
          </el-table-column>
          <el-table-column :label="$t('providerDialog.fieldType')" width="120">
            <template slot-scope="scope">
              <el-select v-if="scope.row.editing" v-model="scope.row.type" size="mini">
                <el-option :label="$t('providerDialog.stringType')" value="string"></el-option>
                <el-option :label="$t('providerDialog.numberType')" value="number"></el-option>
                <el-option :label="$t('providerDialog.booleanType')" value="boolean"></el-option>
                <el-option :label="$t('providerDialog.dictType')" value="dict"></el-option>
                <el-option :label="$t('providerDialog.arrayType')" value="array"></el-option>
                <el-option :label="$t('providerDialog.ragType')" value="rag"></el-option>
              </el-select>
              <span v-else>{{ getFieldTypeName(scope.row.type) }}</span>
            </template>
          </el-table-column>
          <el-table-column :label="$t('providerDialog.defaultValue')" min-width="120">
            <template slot-scope="scope">
              <el-input v-if="scope.row.editing" v-model="scope.row.defaultValue" size="mini"></el-input>
              <span v-else>{{ scope.row.defaultValue }}</span>
            </template>
          </el-table-column>
          <el-table-column :label="$t('providerDialog.operation')" width="100" align="center">
            <template slot-scope="scope">
              <div class="action-buttons">
                <el-button v-if="scope.row.editing" type="text" @click="saveField(scope.$index)">{{ $t('providerDialog.complete') }}</el-button>
                <el-button v-else type="text" @click="editField(scope.$index)">{{ $t('providerDialog.edit') }}</el-button>
                <el-button type="text" class="delete-btn" @click="deleteField(scope.$index)">{{ $t('providerDialog.delete') }}</el-button>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">{{ $t('common.cancel') }}</el-button>
        <el-button type="primary" @click="submitForm" :loading="submitLoading">{{ $t('common.save') }}</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import request from '@/utils/request'
import { hasPermission } from '@/utils/permission'

export default {
  name: 'ProviderManagement',
  data() {
    return {
      loading: false,
      submitLoading: false,
      providerList: [],
      total: 0,
      currentPage: 1,
      pageSize: 10,
      searchForm: {
        modelType: '',
        name: ''
      },
      selectedItems: [],
      dialogVisible: false,
      dialogTitle: '',
      providerForm: {
        id: null,
        modelType: '',
        providerCode: '',
        name: '',
        fieldConfig: '[]',
        sort: 0
      },
      fieldList: [],
      selectedFields: [],
      rules: {
        modelType: [{ required: true, message: this.$t('providerDialog.requiredCategory'), trigger: 'change' }],
        providerCode: [{ required: true, message: this.$t('providerDialog.requiredCode'), trigger: 'blur' }],
        name: [{ required: true, message: this.$t('providerDialog.requiredName'), trigger: 'blur' }]
      }
    }
  },
  created() {
    this.getList()
  },
  computed: {
    visibleActionCount() {
      let count = 0
      if (this.hasPermission('sys:modelprovider:update')) count += 1
      if (this.hasPermission('sys:modelprovider:delete')) count += 1
      return count
    },
    hasAnyActionPermission() {
      return this.visibleActionCount > 0
    },
    actionColumnWidth() {
      const count = this.visibleActionCount
      const widthMap = { 1: 100, 2: 170 }
      return widthMap[count] || 0
    }
  },
  methods: {
    hasPermission,
    getModelTypeLabel(type) {
      if (!type) return ''
      const typeUpper = type.toUpperCase()
      const label = this.$t(`providerManagement.modelType.${typeUpper}`)
      // 如果翻译后的文本不等于 key 本身，说明找到了翻译
      return (label && label !== `providerManagement.modelType.${typeUpper}`) ? label : type.toUpperCase()
    },
    async getList() {
      this.loading = true
      try {
        const res = await request({
          url: '/admin/models/provider/page',
          method: 'get',
          params: {
            page: this.currentPage,
            limit: this.pageSize,
            modelType: this.searchForm.modelType,
            name: this.searchForm.name
          }
        })
        if (res) {
          this.providerList = res.list || []
          this.total = res.total || 0
        }
      } catch (error) {
        console.error('Fetch provider list error:', error)
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.currentPage = 1
      this.getList()
    },
    handleReset() {
      this.searchForm.modelType = ''
      this.searchForm.name = ''
      this.handleSearch()
    },
    handleSelectionChange(val) {
      this.selectedItems = val
    },
    handleSizeChange(val) {
      this.pageSize = val
      this.getList()
    },
    handleCurrentChange(val) {
      this.currentPage = val
      this.getList()
    },
    parseFieldConfig(config) {
      try {
        return JSON.parse(config || '[]')
      } catch (e) {
        return []
      }
    },
    getFieldTypeName(type) {
      const typeMap = {
        string: this.$t('providerDialog.stringType'),
        number: this.$t('providerDialog.numberType'),
        boolean: this.$t('providerDialog.booleanType'),
        dict: this.$t('providerDialog.dictType'),
        array: this.$t('providerDialog.arrayType'),
        rag: this.$t('providerDialog.ragType')
      }
      return typeMap[type] || type
    },
    handleAdd() {
      this.dialogTitle = this.$t('common.add')
      this.providerForm = { id: null, modelType: '', providerCode: '', name: '', fieldConfig: '[]', sort: 0 }
      this.fieldList = []
      this.dialogVisible = true
      this.$nextTick(() => { this.$refs.providerForm.clearValidate() })
    },
    handleEdit(row) {
      this.dialogTitle = this.$t('common.edit')
      this.providerForm = { ...row }
      this.fieldList = this.parseFieldConfig(row.fieldConfig).map(f => ({ ...f, editing: false }))
      this.dialogVisible = true
    },
    // Field Config operations
    addField() {
      if (this.fieldList.some(f => f.editing)) {
        this.$message.warning(this.$t('providerDialog.completeFieldEdit'))
        return
      }
      this.fieldList.push({ key: '', label: '', type: 'string', defaultValue: '', editing: true })
    },
    editField(index) {
      this.fieldList[index].editing = true
    },
    saveField(index) {
      this.fieldList[index].editing = false
    },
    deleteField(index) {
      this.$confirm(this.$t('providerDialog.confirmDeleteField'), this.$t('common.tips'), {
        confirmButtonText: this.$t('common.confirm'),
        cancelButtonText: this.$t('common.cancel'),
        type: 'warning'
      }).then(() => {
        this.fieldList.splice(index, 1)
      })
    },
    handleFieldSelectionChange(val) {
      this.selectedFields = val
    },
    batchDeleteFields() {
      this.$confirm(this.$t('providerDialog.confirmBatchDeleteFields', { count: this.selectedFields.length }), this.$t('common.tips'), {
        confirmButtonText: this.$t('common.confirm'),
        cancelButtonText: this.$t('common.cancel'),
        type: 'warning'
      }).then(() => {
        this.fieldList = this.fieldList.filter(f => !this.selectedFields.includes(f))
        this.selectedFields = []
      })
    },
    async submitForm() {
      this.$refs.providerForm.validate(async (valid) => {
        if (valid) {
          if (this.fieldList.some(f => f.editing)) {
            this.$message.warning(this.$t('providerDialog.completeFieldEdit'))
            return
          }
          this.submitLoading = true
          try {
            const isEdit = !!this.providerForm.id
            const submitData = {
              ...this.providerForm,
              fieldConfig: JSON.stringify(this.fieldList.map(({ editing, ...rest }) => rest))
            }
            const res = await request({
              url: `/admin/models/provider/${isEdit ? 'update' : 'save'}`,
              method: isEdit ? 'put' : 'post',
              data: submitData
            })
            if (res !== undefined) {
              this.$message.success(this.$t('common.saveSuccess'))
              this.dialogVisible = false
              this.getList()
            }
          } catch (error) {
            console.error('Submit provider error:', error)
          } finally {
            this.submitLoading = false
          }
        }
      })
    },
    handleDelete(row) {
      this.$confirm(this.$t('common.confirmDelete'), this.$t('common.tips'), {
        confirmButtonText: this.$t('common.confirm'),
        cancelButtonText: this.$t('common.cancel'),
        type: 'warning'
      }).then(async () => {
        try {
          const res = await request({
            url: '/admin/models/provider/delete',
            method: 'post',
            data: [row.id]
          })
          if (res !== undefined) {
            this.$message.success(this.$t('common.deleteSuccess'))
            this.getList()
          }
        } catch (error) {
          console.error('Delete provider error:', error)
        }
      })
    },
    handleBatchDelete() {
      const ids = this.selectedItems.map(item => item.id)
      this.$confirm(this.$t('providerManagement.confirmDelete', { count: ids.length }), this.$t('common.tips'), {
        confirmButtonText: this.$t('common.confirm'),
        cancelButtonText: this.$t('common.cancel'),
        type: 'warning'
      }).then(async () => {
        try {
          const res = await request({
            url: '/admin/models/provider/delete',
            method: 'post',
            data: ids
          })
          if (res !== undefined) {
            this.$message.success(this.$t('common.deleteSuccess'))
            this.selectedItems = []
            this.getList()
          }
        } catch (error) {
          console.error('Batch delete provider error:', error)
        }
      })
    }
  }
}
</script>

<style scoped lang="scss">
@import "@/styles/list-page.scss";

/* 模型提供商页面特有样式 */
.list-page-container {
  .filter-form {
    .filter-input { width: 220px; }
  }

  .custom-table {
    .view-fields-btn {
      font-weight: 600;
      color: #409eff;
      &:hover { text-decoration: underline; }
    }
  }

  .form-row {
    display: flex;
    gap: 20px;
  }

  .field-actions {
    margin-bottom: 10px;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
  }
}

.custom-dialog {
  border-radius: 12px;
  :deep(.el-dialog__header) {
    padding: 20px 24px;
    border-bottom: 1px solid #f1f5f9;
    .el-dialog__title { font-weight: 600; }
  }
  :deep(.el-dialog__body) { padding: 24px; }
  :deep(.el-dialog__footer) { padding: 16px 24px; border-top: 1px solid #f1f5f9; background-color: #f8fafc; }
  .dialog-content {
    .el-form-item {
      margin-bottom: 20px;
      :deep(.el-form-item__label) { font-size: 13px; font-weight: 500; color: #64748b; padding-bottom: 8px; }
    }
    :deep(.el-input__inner), :deep(.el-textarea__inner) { border-radius: 8px; border-color: #e2e8f0; }
  }
}

.custom-table-small {
  &::before { display: none !important; }
  :deep(.el-table__header-wrapper) {
    th {
      background-color: #f8fafc !important;
      color: #475569;
      font-weight: 600;
      height: 40px;
    }
  }
}
</style>
